import React, { useEffect } from "react";
import { Box, Typography, Stack, Button, Alert, CircularProgress } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import PricingPage from "../../../components/organisms/PricingPage";
import useOrganizationSubscription from "../../../hooks/useOrganizationSubscription";
import NewColors from "../../../themes/NewColors";
import logoImage from "../../../assets/Logo.png";

const SubscribePage = () => {
  const [uid, setUid] = React.useState(() => localStorage.getItem("uid"));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Ensure uid is available - retry if not found initially (might be a timing issue on page load)
  React.useEffect(() => {
    if (!uid) {
      const storedUid = localStorage.getItem("uid");
      if (storedUid) {
        setUid(storedUid);
      }
    }
  }, [uid]);

  const { organizationData: subscribeOrg, loading, error } = useOrganizationSubscription({ uid });
  
  const success = searchParams.get("success");
  const canceled = searchParams.get("canceled");
  const sessionId = searchParams.get("session_id");

  // Clear URL params after showing the message for a few seconds
  useEffect(() => {
    if (success || canceled) {
      const timer = setTimeout(() => {
        // Remove the query params but keep the path
        setSearchParams({});
      }, 5000); // Clear after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [success, canceled, setSearchParams]);

  return (
    <Box style={{ margin: 12 }}>
      <Stack>
        <Stack direction={"row"} justifyContent="flex-start" sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/home")}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: NewColors.text.primary,
              borderColor: NewColors.border.medium,
              "&:hover": {
                borderColor: NewColors.triadicPower.primary,
                backgroundColor: NewColors.triadicPower.interactive.hover,
              },
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Back to Mighty45 Console
            </Typography>
          </Button>
        </Stack>

        <Box style={{ margin: 20 }}>
          <img
            alt="Mighty45 Logo"
            style={{
              alignItems: "center",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            width="146px"
            height="78px"
            src={logoImage}
            onClick={() => {
              window.open("/home");
            }}
          />
        </Box>

        <Box
          style={{
            minHeight: "50vh",
            backgroundColor: NewColors.sectionBackground,
            padding: 24,
          }}
        >
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">Subscription Successful!</Typography>
              <Typography>
                Your subscription has been activated. You now have access to all
                premium features.
              </Typography>
            </Alert>
          )}
          {canceled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6">Checkout Canceled</Typography>
              <Typography>
                Your checkout was canceled. You can try again anytime.
              </Typography>
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6">Error Loading Subscription</Typography>
              <Typography>{error}</Typography>
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
              <CircularProgress />
            </Box>
          ) : subscribeOrg ? (
            <PricingPage organizationData={subscribeOrg} />
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No organization data available
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default SubscribePage;
